import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { Save, Upload, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API } from "@/lib/api";
import { Card, btn } from "@/components/common/Ornament";
import FinancialForm from "@/components/financial/FinancialForm";
import { ListEditor } from "./ContentTab";
import { groupCost } from "@/components/financial/CostDetail";
import { usd } from "@/lib/format";

function CostDetailEditor() {
  const { data, mutate } = useSWR("/content");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data && !items) setItems(data.cost_detail || []);
  }, [data, items]);
  if (!items) return null;
  const save = async () => {
    setBusy(true);
    try {
      const clean = items.filter((x) => x.item).map((x) => ({ ...x, amount: Number(x.amount) || 0 }));
      const { data: d } = await api.put("/admin/content", { cost_detail: clean });
      mutate(d, false);
      toast.success("Cost detail saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card eyebrow="Financial Model Page" title="Construction Cost Detail" testId="admin-cost-detail"
      right={<span className="font-mono-num text-sm text-[#0F2E23]" data-testid="admin-cost-detail-total">Total {usd(groupCost(items).total)}</span>}>
      <p className="text-xs text-slate-500 mb-3">Line items shown to investors under the cost breakdown. Rows with the same section are grouped with a subtotal. This table is for display; update the baseline inputs below to change the model.</p>
      <ListEditor items={items} onChange={setItems} blank={{ section: "Add-Ons", item: "", amount: 0 }} testId="admin-cost"
        fields={[["section", "Section", "sm:w-48"], ["item", "Line item", "sm:w-96"], ["amount", "Amount (USD)", "w-40", "number"]]} />
      <button onClick={save} disabled={busy} className={btn.gold + " mt-4"} data-testid="admin-cost-detail-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save cost detail</button>
    </Card>
  );
}

export function TemplateLinks({ kind }) {
  return (
    <div className="flex flex-wrap gap-2">
      <a href={`${API}/admin/templates/${kind}?fmt=csv`} className={btn.ghost + " !h-9"} data-testid={`admin-${kind}-template-csv`}><FileDown className="h-4 w-4" /> CSV template</a>
      <a href={`${API}/admin/templates/${kind}?fmt=xlsx`} className={btn.ghost + " !h-9"} data-testid={`admin-${kind}-template-xlsx`}><FileDown className="h-4 w-4" /> XLSX template</a>
    </div>
  );
}

export default function FinancialsTab() {
  const { data, mutate } = useSWR("/financials");
  const [vals, setVals] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (data) setVals({ ...data.baseline });
  }, [data]);
  if (!vals) return <Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" />;

  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/financials/baseline", vals); mutate(); toast.success("Baseline saved"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try { const { data: d } = await api.post("/admin/financials/upload", fd); mutate(); toast.success(`Baseline updated (${d.updated} values)`); } catch (e) { toast.error(errMsg(e)); } finally { ref.current.value = ""; }
  };

  return (
    <div className="space-y-6">
      <Card eyebrow="Bulk Update" title="Upload Baseline from Spreadsheet" testId="admin-financials-upload">
        <p className="text-sm text-slate-600 mb-4">Download the template (pre-filled with the current baseline), edit the <code>value</code> column, then upload it. Percentages are entered as numbers (e.g., 74 for 74%).</p>
        <div className="flex flex-wrap gap-2">
          <TemplateLinks kind="financials" />
          <input ref={ref} type="file" accept=".csv,.xlsx" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="admin-financials-file-input" />
          <button onClick={() => ref.current.click()} className={btn.primary + " !h-9"} data-testid="admin-financials-upload-btn"><Upload className="h-4 w-4" /> Upload CSV / XLSX</button>
        </div>
      </Card>
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <FinancialForm values={vals} onChange={setVals} prefix="admin-baseline" />
        <div className="lg:sticky lg:top-6 paper rounded-lg p-6">
          <p className="eyebrow">Baseline</p>
          <h3 className="font-display text-2xl text-[#0F2E23] font-semibold mt-1">Save to update every viewer's starting point</h3>
          <p className="text-sm text-slate-600 mt-2">Viewers' "Reset to Baseline" and the Overview figures use these values.</p>
          <button onClick={save} disabled={busy} className={btn.gold + " mt-5"} data-testid="admin-save-baseline-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save baseline</button>
        </div>
      </div>
      <CostDetailEditor />
    </div>
  );
}
